import { useMemo, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { Delete, PersonAdd } from '@mui/icons-material';
import { MRT_ColumnDef, MaterialReactTable, useMaterialReactTable } from 'material-react-table';
import moment from 'moment';
import { User } from '../../types/users';
import UserForm from './UserForm';
import {
  useCreateUserMutation,
  useDeleteUserMutation,
  useUpdateUserMutation,
} from '../../services/users.service';

function roleColor(role: string): 'error' | 'primary' | 'default' {
  if (role === 'admin') return 'error';
  if (role === 'employee') return 'primary';
  return 'default';
}

function initials(name: string) {
  return name
    .split(' ')
    .map((w) => w[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

function UsersTable({ rows }: { rows: User[] }) {
  const [selectedRow, setSelectedRow] = useState<User | null>(null);
  const [openForm, setOpenForm] = useState(false);
  const [edit, setEdit] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<User | null>(null);

  const [updateUser] = useUpdateUserMutation();
  const [createUser] = useCreateUserMutation();
  const [deleteUser] = useDeleteUserMutation();

  const handleCreate = () => {
    setEdit(false);
    setSelectedRow(null);
    setOpenForm(true);
  };

  const handleEdit = (user: User) => {
    setSelectedRow(user);
    setEdit(true);
    setOpenForm(true);
  };

  const handleClose = () => {
    setOpenForm(false);
    setEdit(false);
    setSelectedRow(null);
  };

  const handleSubmitForm = (user: User) => {
    const action = edit && selectedRow ? updateUser({ ...user }) : createUser(user);
    action.then(handleClose);
  };

  const handleDeleteConfirm = () => {
    if (!deleteTarget) return;
    deleteUser(deleteTarget._id).finally(() => setDeleteTarget(null));
  };

  const columns = useMemo<MRT_ColumnDef<User>[]>(
    () => [
      {
        accessorKey: 'username',
        header: 'User',
        Cell: ({ row }) => (
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Avatar sx={{ width: 34, height: 34, fontSize: 13, bgcolor: 'primary.main' }}>
              {initials(row.original.username || '?')}
            </Avatar>
            <Box>
              <Typography variant="body2" fontWeight={600} lineHeight={1.2}>
                {row.original.username}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {row.original.email}
              </Typography>
            </Box>
          </Stack>
        ),
      },
      {
        accessorKey: 'role',
        header: 'Role',
        size: 120,
        Cell: ({ cell }) => (
          <Chip
            label={cell.getValue<string>()}
            size="small"
            color={roleColor(cell.getValue<string>())}
            sx={{ textTransform: 'capitalize', fontWeight: 600 }}
          />
        ),
      },
      {
        accessorKey: 'permissions',
        header: 'Permissions',
        enableSorting: false,
        Cell: ({ row }) => {
          const perms = row.original.permissions ?? [];
          const active = perms.filter((p) => p.actions.length > 0);
          return (
            <Tooltip
              title={active.map((p) => `${p.resource}: ${p.actions.join(', ')}`).join('\n')}
              placement="top">
              <Typography variant="body2" color="text.secondary">
                {active.length} / {perms.length} resources
              </Typography>
            </Tooltip>
          );
        },
      },
      {
        accessorKey: 'createdAt',
        header: 'Created',
        size: 130,
        Cell: ({ cell }) => (
          <Typography variant="body2" color="text.secondary">
            {moment(cell.getValue<string>()).fromNow()}
          </Typography>
        ),
      },
    ],
    [],
  );

  const table = useMaterialReactTable({
    columns,
    data: rows,
    enableRowActions: true,
    positionActionsColumn: 'last',
    renderRowActions: ({ row }) => (
      <Stack direction="row" spacing={0.5}>
        <Button size="small" onClick={() => handleEdit(row.original)}>
          Edit
        </Button>
        <Tooltip title="Delete user">
          <Button
            size="small"
            color="error"
            onClick={() => setDeleteTarget(row.original)}
            startIcon={<Delete fontSize="small" />}
          />
        </Tooltip>
      </Stack>
    ),
    renderTopToolbarCustomActions: () => (
      <Button variant="contained" startIcon={<PersonAdd />} onClick={handleCreate} size="small">
        New User
      </Button>
    ),
    muiTablePaperProps: {
      sx: { boxShadow: '0px 0px 55px 5px rgba(0,0,0,0.05)', borderRadius: '8px' },
    },
    initialState: { density: 'comfortable' },
  });

  return (
    <>
      <MaterialReactTable table={table} />

      {openForm && (
        <UserForm
          submitHanlder={handleSubmitForm}
          edit={edit}
          open={openForm}
          handleClose={handleClose}
          selectedUser={selectedRow}
        />
      )}

      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete user?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Permanently delete <strong>{deleteTarget?.username}</strong>? This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDeleteConfirm}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default UsersTable;
